"use client";

import React, { useState, useEffect } from "react";
import { Phone, MessageCircle as WhatsApp, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
const isValidInternalLink = (href: string | undefined): boolean => {
  if (!href || href === "#") return false;
  if (href.startsWith("#")) return true;
  if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return true;
  const validPaths = ["/", "/HomePage", "/ProfessionalBlogPage", "/ContactPage", "/HomePage2", "/CaregiverServicePagesPage", "/CitySpecificSEOPagesForNursingCareProviderPage", "/ServicesCitiesArticlesPage", "/HomePage3", "/CaregiverServicePagesPage2", "/NurseInTelAvivWithinHoursPage", "/NursingCaregiverInRamatGanWithinHoursPage", "/NursingCareProviderInHaifaWithinFiveHoursPage", "/BlogPage", "/NursingCareNowPage", "/HomePage4", "/NewSeoPagesGenerationPage", "/NursingCaregiverInCityImmediateSolutionWithinHoursPage", "/NursingCareProviderInCityImmediateSolutionWithinFiveHoursPage", "/NursingCaregiverInCityImmediateSolutionWithinFiveHoursPage", "/NursingCareInCityImmediateSolutionWithinFiveHoursPage", "/SeoPagesForNursingCareProviderInJerusalemHaifaPetahTikvaAndNetanyaPage"];
  const path = href.split("#")[0].split("?")[0].replace(/\/$/, "") || "/";
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return validPaths.some(p => p.toLowerCase() === normalizedPath.toLowerCase());
};
const normalizeInternalLink = (href: string | undefined): string => {
  if (!href || href === "#") return "#";
  if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return href;
  if (href.startsWith("#")) return href;
  const pathMapping: Record<string, string> = {
    "homepage": "/HomePage",
    "page_2": "/ProfessionalBlogPage",
    "page_3": "/ContactPage",
    "homepage2": "/HomePage2",
    "caregiverservicepages": "/CaregiverServicePagesPage",
    "cityspecificseopagesfornursingcareprovider": "/CitySpecificSEOPagesForNursingCareProviderPage",
    "servicescitiesarticles": "/ServicesCitiesArticlesPage",
    "homepage3": "/HomePage3",
    "caregiverservicepagespage2": "/CaregiverServicePagesPage2",
    "nurseintelavivwithinhours": "/NurseInTelAvivWithinHoursPage",
    "nursingcaregiverinramatganwithinhours": "/NursingCaregiverInRamatGanWithinHoursPage",
    "nursingcareproviderinhaifawithinfivehours": "/NursingCareProviderInHaifaWithinFiveHoursPage",
    "blog": "/BlogPage",
    "nursingcarenow": "/NursingCareNowPage",
    "homepage4": "/HomePage4",
    "newseopagesgeneration": "/NewSeoPagesGenerationPage",
    "nursingcaregiverincityimmediatesolutionwithinhours": "/NursingCaregiverInCityImmediateSolutionWithinHoursPage",
    "nursingcareproviderincityimmediatesolutionwithinfivehours": "/NursingCareProviderInCityImmediateSolutionWithinFiveHoursPage",
    "nursingcaregiverincityimmediatesolutionwithinfivehours": "/NursingCareProviderInCityImmediateSolutionWithinFiveHoursPage",
    "nursingcareincityimmediatesolutionwithinfivehours": "/NursingCareInCityImmediateSolutionWithinFiveHoursPage",
    "seopagesfornursingcareproviderinjerusalemhaifapetahtikvaandnetanya": "/SeoPagesForNursingCareProviderInJerusalemHaifaPetahTikvaAndNetanyaPage",
    "nursingcareathome": "/CaregiverServicePagesPage",
    "hospitalcare": "/CaregiverServicePagesPage",
    "privatenursingcare": "/CaregiverServicePagesPage",
    "nightcaregiver": "/CaregiverServicePagesPage"
  };
  const cleanHref = href.replace(/[^a-z0-9]/gi, "").toLowerCase();
  if (pathMapping[cleanHref]) {
    return pathMapping[cleanHref];
  }
  const normalized = href.startsWith("/") ? href : `/${href}`;
  return isValidInternalLink(normalized) ? normalized : "#";
};
const MENU_ORDER = ["דף הבית", "Home", "שירותים", "מטפל סיעודי בבית", "השגחה בבית חולים", "אזורי שירות", "מאמרים", "הבלוג המקצועי", "צור קשר"];
const BOTTOM_MENU_LABELS = ["City Specific Caregiver Pages", "מטפל סיעודי Tel Aviv Haifa Jerusalem", "מטפל סיעודי ב שם העיר שירות מיידי תוך 5 שעות"];
const isBottomMenuItem = (label: string) => {
  const normalized = label.replace(/\s+/g, ' ').trim();
  return BOTTOM_MENU_LABELS.some(l => l.trim() === normalized);
};
const sortMenuItems = (items: any[]) => {
  return [...items].sort((a, b) => {
    const indexA = MENU_ORDER.findIndex(label => a.label.includes(label) || label.includes(a.label));
    const indexB = MENU_ORDER.findIndex(label => b.label.includes(label) || label.includes(b.label));
    const finalA = indexA === -1 ? 999 : indexA;
    const finalB = indexB === -1 ? 999 : indexB;
    return finalA - finalB;
  });
};
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
            {[1, 2, 3, 4, 5, 6, 7].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 bg-muted animate-pulse rounded-md" />)}
        </div>;
  }
  const sortedItems = sortMenuItems(menuItems);
  return <NavigationMenu viewport={false}>
        <NavigationMenuList className="gap-0 lg:gap-1">
            {sortedItems.filter(item => isValidInternalLink(item.href) && !isBottomMenuItem(item.label)).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                    <Link to={normalizeInternalLink(item.href)} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-3 lg:px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50 text-foreground whitespace-nowrap">
                        {item.label}
                    </Link>
                </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  closeMenu
}: {
  closeMenu: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2">
            {[1, 2, 3, 4, 5, 6, 7].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
        </div>;
  }
  const sortedItems = sortMenuItems(menuItems);
  return <div className="flex flex-col space-y-1">
        {sortedItems.filter(item => isValidInternalLink(item.href) && !isBottomMenuItem(item.label)).map((item, i) => <div key={item.id} data-index={i}>
            <Link to={normalizeInternalLink(item.href)} className="block px-4 py-3 text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors" onClick={closeMenu}>
                {item.label}
            </Link>
        </div>)}
    </div>;
}
function BottomMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  const items = menuItems.filter(item => isBottomMenuItem(item.label) && isValidInternalLink(item.href));
  if (items.length === 0) return null;
  return <div className="flex flex-wrap justify-center gap-2 md:gap-4 py-4 border-t border-border mt-4">
        {items.map((item, i) => <Link key={item.id} data-index={i} to={normalizeInternalLink(item.href)} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-3 lg:px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground text-foreground whitespace-nowrap">
            {item.label}
        </Link>)}
    </div>;
}
export default function TopNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const hrefAttr = anchor.getAttribute("href");
        if (!hrefAttr) return;
        const href = normalizeInternalLink(hrefAttr);
        const currentPath = window.location.pathname.replace(/\/$/, "") || "/";
        if (href === "#" || href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let targetHash = "";
        if (href.startsWith("#")) {
          targetHash = href;
        } else if (href.includes("#")) {
          const [pathPart, hashPart] = href.split("#");
          const normalizedPathPart = pathPart.replace(/\/$/, "") || "/";
          if (normalizedPathPart === currentPath || normalizedPathPart === "/") {
            targetHash = hashPart ? `#${hashPart}` : "#";
          }
        }
        if (targetHash) {
          if (targetHash === "#") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            setIsMobileMenuOpen(false);
          } else {
            const element = document.querySelector(targetHash);
            if (element) {
              e.preventDefault();
              element.scrollIntoView({
                behavior: "smooth",
                block: "start"
              });
              setIsMobileMenuOpen(false);
            }
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="ניווט-עליון" className={cn("sticky top-0 z-50 w-full transition-all duration-300 bg-white border-b border-border shadow-md", isScrolled ? "py-0" : "py-1")}>
        <div className="container mx-auto px-4">

            <div className="flex items-center justify-between py-2 border-b border-border/50 gap-2 md:gap-4">
                <div className="flex items-center gap-2 md:gap-3">
                    <Button asChild className="bg-chart-2 text-white hover:bg-chart-2/90 font-default shadow-sm transition-all text-xs md:text-sm h-8 md:h-9">
                        <Link to="https://wa.me/972501234567" newTab>
                            <WhatsApp className="ml-1 md:ml-2 h-3 w-3 md:h-4 md:w-4" />
                            וואטסאפ – מענה מיידי
                        </Link>
                    </Button>
                    <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/5 font-default text-xs md:text-sm h-8 md:h-9">
                        <Link to="tel:0501234567">
                            <Phone className="ml-1 md:ml-2 h-3 w-3 md:h-4 md:w-4" />
                            התקשר עכשיו – מענה תוך דקות
                        </Link>
                    </Button>
                </div>
            </div>


            <div className="flex h-16 gap-4 items-center justify-between md:h-20">

                <div className="flex-1 flex items-center justify-start">
                    <Link to="/">
                        <WvcLogo className="h-7 w-auto" />
                    </Link>
                </div>


                <div className="hidden md:flex flex-[2] justify-center">
                    <MenuProvider menu_id="19">
                        <DesktopMenu />
                    </MenuProvider>
                </div>


                <div className="flex-1 flex items-center justify-end gap-3">
                    <Button asChild className="hidden lg:flex bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-lg transition-all">
                        <Link to="tel:0501234567">
                            קבל מטפל תוך שעות
                        </Link>
                    </Button>

                    <div className="flex items-center md:hidden gap-3">
                        <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-expanded={isMobileMenuOpen} aria-label="Toggle navigation menu" className="text-foreground hover:bg-accent">
                            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </Button>
                    </div>
                </div>
            </div>


            <div className="pb-2 text-center">
                <p className="text-[10px] md:text-xs font-bold text-chart-2 tracking-wide">
                    מטפלים ישראלים בלבד זמינות תוך 5 שעות
                </p>
            </div>
            

            {isMobileMenuOpen && <div className="md:hidden py-4 border-t border-border animate-in slide-in-from-top-2 fade-in-0 duration-200">
                <MenuProvider menu_id="19">
                    <MobileMenu closeMenu={() => setIsMobileMenuOpen(false)} />
                </MenuProvider>

                <div className="flex flex-col gap-3 mt-6 px-2">
                    <Button asChild className="w-full justify-center bg-primary text-white hover:bg-primary/90 font-bold text-base h-12 shadow-md">
                        <Link to="tel:0501234567">
                            קבל מטפל תוך שעות
                        </Link>
                    </Button>
                </div>
            </div>}
        </div>


        <Button asChild className="fixed bottom-6 right-6 z-[60] md:hidden size-14 rounded-lg bg-chart-2 text-white shadow-2xl hover:bg-chart-2/90 p-0 flex items-center justify-center animate-pulse">
            <Link to="https://wa.me/972501234567" newTab aria-label="WhatsApp">
                <WhatsApp className="h-7 w-7" />
            </Link>
        </Button>
    </section>;
}